import { createBrowserRouter } from "react-router";
import { useTranslation } from "react-i18next";
import { AuthGuard, OnboardingGuard } from "../auth/auth-guard";
import { AppShell } from "../layouts/app-shell";
import { SignInPage } from "../pages/sign-in";
import { NotFoundPage } from "../pages/not-found";
import { LandingGuard } from "../pages/landing/landing-guard";
import { env } from "../config/env";

/** Placeholder component for routes not yet implemented */
function Placeholder({ textKey }: { textKey: string }): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center p-8">
      <p className="text-lg text-foreground-muted">{t(textKey)}</p>
    </div>
  );
}

// Lazy-load dev-only routes
const devRoutes = env.isDev
  ? [
      {
        path: "/dev/styleguide",
        lazy: async () => {
          const { StyleGuidePage } = await import("../pages/dev/styleguide");
          return { Component: StyleGuidePage };
        },
      },
    ]
  : [];

export const router = createBrowserRouter([
  // Public routes
  { path: "/", element: <LandingGuard /> },
  { path: "/sign-in", element: <SignInPage /> },
  {
    path: "/auth/verify",
    lazy: async () => {
      const { VerifyMagicLink } = await import("../pages/auth/verify-magic-link");
      return { Component: VerifyMagicLink };
    },
  },
  {
    path: "/auth/callback",
    lazy: async () => {
      const { OAuthCallback } = await import("../pages/auth/oauth-callback");
      return { Component: OAuthCallback };
    },
  },

  // Protected routes: Onboarding flow (Feature 034)
  // Uses OnboardingGuard: authenticated + NOT onboarded → render, already onboarded → /home
  {
    element: <OnboardingGuard />,
    children: [
      {
        path: "/onboarding",
        lazy: async () => {
          const { OnboardingLayout } = await import("../pages/onboarding");
          return { Component: OnboardingLayout };
        },
        children: [
          {
            path: "profile",
            lazy: async () => {
              const { ProfileStep } = await import("../pages/onboarding/steps/profile-step");
              return { Component: ProfileStep };
            },
          },
          {
            path: "integrations",
            lazy: async () => {
              const { IntegrationSelectionStep } = await import("../pages/onboarding/steps/integration-selection-step");
              return { Component: IntegrationSelectionStep };
            },
          },
          {
            path: "connect",
            lazy: async () => {
              const { ConnectionStep } = await import("../pages/onboarding/steps/connection-step");
              return { Component: ConnectionStep };
            },
          },
          {
            path: "teams",
            lazy: async () => {
              const { TeamDiscoveryStep } = await import("../pages/onboarding/steps/team-discovery-step");
              return { Component: TeamDiscoveryStep };
            },
          },
          {
            path: "status",
            lazy: async () => {
              const { PerspectiveStep } = await import("../pages/onboarding/steps/perspective-step");
              return { Component: PerspectiveStep };
            },
          },
        ],
      },
    ],
  },

  // Protected routes (wrapped in AuthGuard → AppShell)
  {
    element: <AuthGuard />,
    children: [
      // Standalone auth pages (no AppShell chrome)
      {
        path: "/auth/slack/link",
        lazy: async () => {
          const { SlackLinkPage } = await import("../pages/auth/slack-link");
          return { Component: SlackLinkPage };
        },
      },
      {
        element: <AppShell />,
        children: [
          { path: "/home", element: <Placeholder textKey="placeholder.home" /> },
          {
            path: "/settings/slack-bot",
            lazy: async () => {
              const { SlackBotSettingsPage } = await import(
                "../pages/settings/slack-bot"
              );
              return { Component: SlackBotSettingsPage };
            },
          },
        ],
      },
    ],
  },

  // Dev-only routes (excluded from production builds)
  ...devRoutes,

  // Catch-all 404
  { path: "*", element: <NotFoundPage /> },
]);
